import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export interface Crew {
  id: string;
  name: string;
  description: string;
  status: 'idle' | 'running' | 'completed' | 'error';
  progress: number;
  created_at?: string;
  updated_at?: string;
  agents?: string[];
  tasks?: string[];
}

export function useCrews() {
  const [crews, setCrews] = useState<Crew[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchCrews = async () => {
    if (!user) return;
    
    try {
      const { data: crewsData, error: crewsError } = await supabase
        .from('crews')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (crewsError) throw crewsError;

      // Fetch crew agents and tasks
      const crewsWithDetails = await Promise.all(
        (crewsData || []).map(async (crew) => {
          const [agentsRes, tasksRes] = await Promise.all([
            supabase.from('crew_agents').select('agent_id').eq('crew_id', crew.id),
            supabase.from('crew_tasks').select('task_id').eq('crew_id', crew.id)
          ]);

          return {
            ...crew,
            agents: agentsRes.data?.map(ca => ca.agent_id) || [],
            tasks: tasksRes.data?.map(ct => ct.task_id) || []
          };
        })
      );

      setCrews(crewsWithDetails);
    } catch (error) {
      console.error('Error fetching crews:', error);
      toast({
        title: "Error",
        description: "Failed to load crews",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createCrew = async (crewData: Omit<Crew, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user) return;

    try {
      const { data: crew, error: crewError } = await supabase
        .from('crews')
        .insert([{
          name: crewData.name,
          description: crewData.description,
          status: crewData.status,
          progress: crewData.progress,
          user_id: user.id,
        }])
        .select()
        .single();

      if (crewError) throw crewError;

      // Add agents to crew
      if (crewData.agents && crewData.agents.length > 0) {
        const agentInserts = crewData.agents.map(agentId => ({
          crew_id: crew.id,
          agent_id: agentId
        }));
        
        const { error: agentError } = await supabase
          .from('crew_agents')
          .insert(agentInserts);
        
        if (agentError) throw agentError;
      }

      // Add tasks to crew
      if (crewData.tasks && crewData.tasks.length > 0) {
        const taskInserts = crewData.tasks.map(taskId => ({
          crew_id: crew.id,
          task_id: taskId
        }));
        
        const { error: taskError } = await supabase
          .from('crew_tasks')
          .insert(taskInserts);
        
        if (taskError) throw taskError;
      }
      
      const newCrew = {
        ...crew,
        agents: crewData.agents || [],
        tasks: crewData.tasks || []
      };
      
      setCrews(prev => [newCrew, ...prev]);
      toast({
        title: "Success",
        description: "Crew created successfully",
      });
      
      return newCrew;
    } catch (error) {
      console.error('Error creating crew:', error);
      toast({
        title: "Error",
        description: "Failed to create crew",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateCrew = async (id: string, updates: Partial<Crew>) => {
    try {
      const { data, error } = await supabase
        .from('crews')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setCrews(prev => prev.map(crew => crew.id === id ? { ...crew, ...data } : crew));
      toast({
        title: "Success",
        description: "Crew updated successfully",
      });
      
      return data;
    } catch (error) {
      console.error('Error updating crew:', error);
      toast({
        title: "Error",
        description: "Failed to update crew",
        variant: "destructive",
      });
      throw error;
    }
  };

  const startCrew = async (id: string) => {
    return updateCrew(id, { status: 'running', progress: 0 });
  };

  const stopCrew = async (id: string) => {
    return updateCrew(id, { status: 'idle' });
  };

  useEffect(() => {
    fetchCrews();
  }, [user]);

  return {
    crews,
    loading,
    createCrew,
    updateCrew,
    startCrew,
    stopCrew,
    refetch: fetchCrews
  };
}